import type { WorkflowRunNode, ConnectorType, NodePromptSource, WorkflowNodeOutput } from '@/types'
import { NodeStatusBadge, NodeTypeIcon } from '../shared'
import { LogViewer } from './LogViewer'
import { MemoryViewer } from './MemoryViewer'
import { AgentNodeLineageBanner } from './AgentNodeLineageBanner'
import { RetryInfoBlock } from './RetryInfoBlock'
import { NodePromptLink } from './NodePromptLink'
import { Button, Badge } from '@/components/ui'
import { TabBar } from '@/components/TabBar'
import { CopyButton } from '@/components/ui/copy-button'
import { hasCopyableContent, renderOutputValue } from './nodeOutputValue'
import { X, RotateCw, SkipForward, Terminal, Clock, FileText, Bot, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useWorkflowRunNodeContent } from '@/hooks'
import { formatDurationMs } from '@/lib/timeFormatters'
import { useReconnectConnector } from '@/hooks/useReconnectConnector'
import { TokenDialog } from '@/components/projects/connectors'
import { CONNECTOR_LABELS } from '@/components/projects/connectors/connectorConstants'
import {
  ConnectorStatuses,
  ValidateConnectorOutputs,
  ValidateConnectorTaskType,
  WorkflowErrorCodes,
} from '@/lib/workflowErrorCodes'

type Tab = 'logs' | 'inputs' | 'outputs' | 'container' | 'memory'

interface Props {
  node: WorkflowRunNode
  projectId: string
  canIntervene: boolean
  onClose: () => void
  onRetry: (nodeId: string) => void
  onSkip: (nodeId: string) => void
}

function toKnownConnectorType(value: string | null, canReconnect: (type: ConnectorType) => boolean): ConnectorType | null {
  if (!value) return null
  if (!(value in CONNECTOR_LABELS)) return null

  return canReconnect(value as ConnectorType) ? (value as ConnectorType) : null
}

function findOutputValue(outputs: WorkflowNodeOutput[], key: string): string | null {
  return outputs.find((o) => o.key === key)?.value ?? null
}

function getReconnectableConnectorType(
  node: WorkflowRunNode,
  outputs: WorkflowNodeOutput[],
  canReconnect: (type: ConnectorType) => boolean,
): ConnectorType | null {
  if (node.errorCode === WorkflowErrorCodes.ConnectorExpired) {
    return toKnownConnectorType(node.connectorType, canReconnect)
  }

  if (node.taskType === ValidateConnectorTaskType) {
    const status = findOutputValue(outputs, ValidateConnectorOutputs.Status)
    if (status === ConnectorStatuses.Expired) {
      return toKnownConnectorType(findOutputValue(outputs, ValidateConnectorOutputs.ConnectorType), canReconnect)
    }
  }

  return null
}

function PromptSourceBadge({ promptSource }: { promptSource: NodePromptSource }) {
  const { repository, branch, filePath } = promptSource
  const title = [repository, branch ? `@${branch}` : null, filePath].filter(Boolean).join(' ')

  return (
    <Badge variant="outline" className="text-xs flex items-center gap-1" title={title || undefined}>
      <FileText className="h-3 w-3" />
      Prompt: {filePath ?? 'repository'} (node override)
    </Badge>
  )
}

function NodeContentPending({ isError, what, onRetry }: { isError: boolean; what: string; onRetry: () => void }) {
  if (isError) {
    return (
      <div role="alert" className="p-4 h-full bg-card space-y-2">
        <p className="text-sm text-danger-foreground">Couldn't load this node's {what}.</p>
        <Button size="sm" variant="outline" onClick={onRetry}>
          <RotateCw className="h-3 w-3 mr-1" /> Try again
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center h-full bg-card" role="status" aria-live="polite">
      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      <span className="sr-only">Loading node {what}</span>
    </div>
  )
}

const LINEAGE_BANNER_TASK_TYPES = ['claude-code', 'copilot-cli', 'agent', 'titan']

const rendersLineageBanner = (taskType: string) =>
  LINEAGE_BANNER_TASK_TYPES.includes(taskType.toLowerCase())

export function NodeDetailPanel({ node, projectId, canIntervene, onClose, onRetry, onSkip }: Props) {
  const [tab, setTab] = useState<Tab>('logs')
  const { runId } = useParams()
  const content = useWorkflowRunNodeContent(projectId, runId!, node)
  const outputs = content.data?.outputs ?? []
  const resolvedPrompt = content.data?.resolvedPrompt
  const inputValues = content.data?.inputValues ?? null
  const isRunning = node.status === 'Running' || node.status === 'RetryPending'
  const isFailed = node.status === 'Failed'
  const isValidateConnectorExpired =
    node.taskType === ValidateConnectorTaskType &&
    findOutputValue(outputs, ValidateConnectorOutputs.Status) === ConnectorStatuses.Expired

  const { reconnect, canReconnect, isReconnecting, tokenDialog } = useReconnectConnector({
    projectId,
    onSuccess: () => {
      onRetry(node.id)
    },
  })

  const connectorType = getReconnectableConnectorType(node, outputs, canReconnect)

  const tabs: { key: Tab; label: string }[] = [
    { key: 'logs', label: 'Logs' },
    { key: 'inputs', label: 'Inputs' },
    { key: 'outputs', label: 'Outputs' },
    { key: 'container', label: 'Container' },
    ...(node.taskType === 'agent' ? [{ key: 'memory' as Tab, label: 'Memory' }] : []),
  ]

  const activeTab = tabs.some(t => t.key === tab) ? tab : 'logs'

  const inputsDisplay = resolvedPrompt && inputValues
    ? Object.fromEntries(Object.entries(inputValues).filter(([key]) => key.toLowerCase() !== 'prompt'))
    : inputValues

  return (
    <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden">
      <div className="p-4 border-b shrink-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <NodeTypeIcon type={node.taskType} className="h-5 w-5" />
            <span className="font-semibold">{node.name}</span>
            <NodeStatusBadge status={node.status} />
            <Badge variant="secondary" className="text-xs">{node.taskType}</Badge>
            {node.agent && (
              <Badge
                variant="outline"
                className="gap-1 font-mono text-xs"
                title={`Agent: ${node.agent.displayName}`}
                data-testid="node-agent-badge"
              >
                <Bot className="h-3 w-3" aria-hidden="true" />
                <span className="sr-only">Agent </span>
                {node.agent.name}@{node.agent.version}
              </Badge>
            )}
            {node.durationMs != null && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" /> {formatDurationMs(node.durationMs)}
              </span>
            )}
            {node.promptSource && <PromptSourceBadge promptSource={node.promptSource} />}
            <NodePromptLink node={node} projectId={projectId} />
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={onClose} aria-label="Close node details">
            <X className="h-4 w-4" />
          </Button>
        </div>
        {(node.errorMessage || isValidateConnectorExpired) && (
          <div role="alert" className="mt-2 bg-danger border border-danger-foreground rounded p-2">
            <p className="text-xs text-danger-foreground">
              {node.errorMessage ?? `${connectorType ?? 'Connector'} token is expired or revoked. Reconnect to resume.`}
            </p>
            {connectorType && (
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                disabled={isReconnecting}
                onClick={() => reconnect(connectorType)}
              >
                <RotateCw className="h-3 w-3 mr-1" />
                Reconnect {CONNECTOR_LABELS[connectorType]}
              </Button>
            )}
          </div>
        )}
        {rendersLineageBanner(node.taskType) && node.resolvedModel && (
          <AgentNodeLineageBanner
            resolvedModel={node.resolvedModel}
            resolvedEffort={node.resolvedEffort}
            defaultModel={node.defaultModel}
            defaultEffort={node.defaultEffort}
            defaultSource={node.defaultSource}
          />
        )}
        <RetryInfoBlock node={node} />
        {!node.status.match(/^(Completed|Skipped)$/) && (
          <div className="flex items-center gap-2 mt-3">
            {isFailed && (
                <Button size="sm" variant="outline" className="bg-toolbar text-foreground border-border-accent" disabled={!canIntervene} title={canIntervene ? undefined : 'Only an administrator or the user who started the workflow can retry its nodes.'} onClick={() => onRetry(node.id)}>
                    <RotateCw className="h-3 w-3 mr-1" /> Retry
                </Button>
            )}
            <Button className="bg-toolbar border-border-accent" size="sm" variant="outline" disabled={!canIntervene} title={canIntervene ? undefined : 'Only an administrator or the user who started the workflow can skip its nodes.'} onClick={() => onSkip(node.id)}>
              <SkipForward className="h-3 w-3 mr-1" /> Skip
            </Button>
            {node.containerStrategy !== 'None' && isRunning && (
              <Button size="sm" variant="outline" disabled>
                <Terminal className="h-3 w-3 mr-1" /> Attach
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 p-4 flex flex-col gap-3">
        <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setTab} />

        <div className="flex-1 flex flex-col rounded-lg border overflow-hidden">

          <div className="flex-1 min-h-0">
            {activeTab === 'logs' && (
              <LogViewer node={node} />
            )}
            {(activeTab === 'inputs' || activeTab === 'outputs') && (!content.data || (activeTab === 'outputs' && content.isPlaceholderData && outputs.length === 0)) && (
              <NodeContentPending isError={content.isError} what={activeTab} onRetry={() => content.refetch()} />
            )}
            {activeTab === 'inputs' && content.data && (
              <div className="p-4 overflow-y-auto h-full bg-card space-y-3">
                {resolvedPrompt && (
                  <div className="relative">
                    <div className="text-xs font-semibold mb-1 text-muted-foreground">Prompt (as delivered)</div>
                    <CopyButton text={resolvedPrompt} className="absolute top-6 right-2" tooltipLabel="Copy prompt" />
                    <pre className="text-xs font-mono bg-muted p-3 pr-10 rounded overflow-x-auto whitespace-pre-wrap">
                      {resolvedPrompt}
                    </pre>
                  </div>
                )}
                <div className="relative">
                  <CopyButton text={JSON.stringify(inputsDisplay, null, 2)} className="absolute top-2 right-2" tooltipLabel="Copy inputs" />
                  <pre className="text-xs font-mono bg-muted p-3 pr-10 rounded overflow-x-auto">
                    {JSON.stringify(inputsDisplay, null, 2)}
                  </pre>
                </div>
              </div>
            )}
            {activeTab === 'outputs' && content.data && !(content.isPlaceholderData && outputs.length === 0) && (
              <div className="p-4 overflow-y-auto h-full bg-card">
                {outputs.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {isRunning ? 'Outputs will appear when the node completes.' : 'No outputs.'}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {outputs.map(out => (
                      <div key={out.id} className="border rounded p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold">{out.key}</span>
                          {out.isArtifact && <Badge variant="secondary" className="text-xs">Artifact</Badge>}
                        </div>
                        <div className="relative">
                          {hasCopyableContent(out.value) && (
                            <CopyButton text={out.value} className="absolute top-1 right-2 z-10" tooltipLabel={`Copy ${out.key}`} />
                          )}
                          {renderOutputValue(out.key, out.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'memory' && (
              <MemoryViewer node={node} />
            )}
            {activeTab === 'container' && (
              <div className="p-4 overflow-y-auto h-full space-y-3 bg-card">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-xs text-muted-foreground block">Strategy</span>
                    <span className="font-medium">{node.containerStrategy}</span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block">Status</span>
                    <span className="font-medium">{isRunning ? 'Running' : node.status === 'Completed' ? 'Terminated' : 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block">Image</span>
                    <span className="font-mono text-xs break-all" data-testid="container-image">{node.containerStrategy !== 'None' ? node.imageUri ?? '—' : '—'}</span>
                  </div>
                  <div>
                    <span className="text-xs text-muted-foreground block">Container ID</span>
                    <span className="font-mono text-xs break-all" data-testid="container-id">{node.containerStrategy !== 'None' ? node.containerId ?? '—' : '—'}</span>
                  </div>
                </div>
                {node.containerStrategy !== 'None' && (
                  <div className="mt-4 p-3 bg-neutral border-border rounded border text-xs text-muted-foreground">
                    Interactive container terminal is available in production.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <TokenDialog
        projectId={tokenDialog.projectId}
        connectorType={tokenDialog.connectorType}
        open={tokenDialog.open}
        onOpenChange={tokenDialog.onOpenChange}
        onSave={tokenDialog.onSave}
        isSaving={tokenDialog.isSaving}
        inlineError={tokenDialog.inlineError}
        reconnectConnectionName={tokenDialog.reconnectConnectionName}
      />
    </div>
  )
}

